import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Spinner } from "@/components/ui/spinner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  Users,
  DollarSign,
  TrendingUp,
  LockIcon,
  Frown,
  InfoIcon,
  Code2Icon,
  RefreshCcw,
  PlayCircleIcon,
  PauseIcon,
  SendIcon,
  EyeIcon,
  FlameIcon,
} from "lucide-react";
import { useAuth, useUser } from "@clerk/react";
import tiktok from "@/assets/tiktok.svg";
import instagram from "@/assets/instagram.svg";
import youtube from "@/assets/youtube.svg";
import { useNavigate } from "react-router-dom";
import { useMemo, type ReactNode } from "react";
import { useCampaignStore } from "@/stores";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Skeleton } from "@/components/ui/skeleton";
import { isRepopulatingCampaign } from "@/lib/eve";
import { formatCompact, timeAgo } from "@/lib/format";

type CampaignStatus =
  | "POPULATING"
  | "FAILED"
  | "SEARCH"
  | "OUTREACH"
  | "ONBOARD"
  | "APPROVAL"
  | "COMPLETE";

interface PartnershipTierComponent {
  type: string;
  value?: number;
  description?: string;
  custom_formula?: { expression: string; constants: { name: string; value: number }[] };
}

interface PartnershipTier {
  order: number;
  operator: string | null;
  components: PartnershipTierComponent[];
}

interface CampaignProps {
  campaign_name: string;
  _id: string;
  partnership?: {
    tiers?: PartnershipTier[];
  };
  followers: { from: number; to: number };
  number_of_creators: number;
  creator_prompt: string;
  platform: string;
  status: CampaignStatus;
  owner?: {
    id: string;
    first_name: string;
    last_name: string;
    avatar: string;
  };
  creation_date: string;
  onboarding: boolean;
  loading?: boolean;
  autopilot: boolean;
  messaging: "approval" | "automatic";
  /** Amy v2 runs this campaign (eve_autopilot presence server-side). */
  is_eve?: boolean;
  /** Amy's own automation flag — replaces the legacy autopilot indicator when is_eve. */
  eve_autopilot?: boolean | null;
  /** Stamped while Amy v2 re-runs the creator search on this campaign (cleared by the worker). */
  repopulating_since?: string | null;
}

const STATUS_CONFIG: Record<
  CampaignStatus,
  { progress: number; className: string }
> = {
  POPULATING: { progress: 10, className: "bg-slate-500/10 text-slate-500" },
  FAILED: { progress: 0, className: "bg-red-500/10 text-red-500" },
  SEARCH: { progress: 20, className: "bg-slate-500/10 text-slate-500" },
  OUTREACH: { progress: 40, className: "bg-blue-500/10 text-blue-500" },
  ONBOARD: { progress: 60, className: "bg-amber-500/10 text-amber-500" },
  APPROVAL: { progress: 80, className: "bg-purple-500/10 text-purple-500" },
  COMPLETE: { progress: 100, className: "bg-green-500/10 text-green-500" },
};

const PLATFORM_ICONS: Record<string, string> = { instagram, youtube, tiktok };

const capitalize = (s: unknown) => {
  if (typeof s !== "string") return String(s ?? "");
  return s.charAt(0).toUpperCase() + s.slice(1);
};

// A range's open top end is stored as 1B.
const formatFollowerCount = (count: number) => (count >= 1_000_000_000 ? "Max" : formatCompact(count));

// Replaces all the `loading ? <Skeleton /> : <Content />` blocks
function LoadingOr({
  loading,
  skeleton,
  children,
}: {
  loading?: boolean;
  skeleton: string; // className for Skeleton
  children: ReactNode;
}) {
  return loading ? <Skeleton className={skeleton} /> : <>{children}</>;
}

// Deduplicates the messaging/autopilot/dev indicator buttons
const colorStyles: Record<string, { container: string; icon: string }> = {
  "muted-foreground": {
    container:
      "border-muted-foreground/60 hover:bg-muted-foreground/10 bg-[repeating-linear-gradient(315deg,--theme(--color-muted-foreground/0.2)_0,--theme(--color-muted-foreground/0.2)_1px,transparent_0,transparent_50%)]",
    icon: "text-muted-foreground fill-background",
  },
  success: {
    container:
      "border-success/60 hover:bg-success/10 bg-[repeating-linear-gradient(315deg,--theme(--color-success/0.2)_0,--theme(--color-success/0.2)_1px,transparent_0,transparent_50%)]",
    icon: "text-success fill-background",
  },
  warning: {
    container:
      "border-warning/60 hover:bg-warning/10 bg-[repeating-linear-gradient(315deg,--theme(--color-warning/0.2)_0,--theme(--color-warning/0.2)_1px,transparent_0,transparent_50%)]",
    icon: "text-warning fill-background",
  },
  danger: {
    container:
      "border-danger/60 hover:bg-danger/10 bg-[repeating-linear-gradient(315deg,--theme(--color-danger/0.2)_0,--theme(--color-danger/0.2)_1px,transparent_0,transparent_50%)]",
    icon: "text-danger fill-background",
  },
  "primary": {
    container:
      "border-primary/60 hover:bg-primary/10 bg-[repeating-linear-gradient(315deg,--theme(--color-primary/0.2)_0,--theme(--color-primary/0.2)_1px,transparent_0,transparent_50%)]",
    icon: "text-primary fill-background",
  },
};

function StatusIndicator({
  loading,
  icon: Icon,
  tooltip,
  color = "muted-foreground",
}: {
  loading?: boolean;
  icon: React.ElementType;
  tooltip: string;
  color?: "success" | "warning" | "danger" | "muted-foreground" | "primary";
}) {
  const styles = colorStyles[color];

  return (
    <LoadingOr loading={loading} skeleton="size-8 rounded-md">
      <Tooltip>
        <TooltipTrigger
          className={`size-8 border shrink-0 rounded-md flex items-center justify-center cursor-help bg-size-[7px_7px] ${styles.container}`}
        >
          <Icon className={styles.icon} />
        </TooltipTrigger>
        <TooltipContent>{tooltip}</TooltipContent>
      </Tooltip>
    </LoadingOr>
  );
}

export function CampaignCard({
  campaign_name,
  _id,
  partnership,
  followers,
  number_of_creators,
  platform,
  status = "SEARCH",
  owner,
  creator_prompt,
  creation_date,
  onboarding,
  loading,
  autopilot,
  messaging,
  is_eve,
  eve_autopilot,
  repopulating_since,
}: CampaignProps) {
  const { user } = useUser();
  const { getToken } = useAuth();
  const { deleteCampaign } = useCampaignStore();
  const navigate = useNavigate();

  const isOver15Mins = useMemo(
    () => Date.now() - new Date(creation_date).getTime() > 16 * 60_000,
    [creation_date],
  );

  if (!user) return null;

  const isPopulating = status === "POPULATING";
  // Amy v2 re-running the search on this (existing, possibly live) campaign:
  // show a loading indicator, but keep the card fully usable — no lock
  // overlay, no delete offer.
  const isRepopulating = isRepopulatingCampaign({ repopulating_since });

  const { progress, className: badgeClass } = STATUS_CONFIG[status] ?? { progress: 0, className: "bg-slate-500/10 text-slate-500" };

  async function handleDeleteCampaign() {
    const token = await getToken();
    if (!token || !_id) return;
    await deleteCampaign(_id, token);
    navigate("/dashboard/campaigns");
    window.location.reload();
  }

  const navigateToCampaign = () => {
    if (isPopulating && !isOver15Mins) return;
    navigate(`/dashboard/campaigns/${encodeURIComponent(_id)}/`);
  };

  return (
    <Button
      onClick={navigateToCampaign}
      // disabled={isPopulating && !isOver15Mins}
      variant="outline"
      card
      className={`relative group/card flex flex-col h-48 w-full p-0.5 border-none gap-0.25 rounded-2xl justify-between transition-none shadow-none bg-muted-foreground/10 hoer:bg-muted-foreground/10 dark:bg-sidebar hover:dark:bg-sidebar`}
    >
      {isPopulating && (
        <div className="absolute inset-0 bg-background/20 backdrop-blur-sm rounded-2xl flex flex-col gap-2 items-center justify-center z-10 opacity-0 group-hover/card:bg-sidebar transition-none duration-100 border">
          {isOver15Mins ? (
            <>
              <span className="font-medium text-foreground text-sm flex items-center gap-2 mb-2">
                <Frown className="size-4" />
                Something went wrong!
              </span>
              <Button
                size="sm"
                variant="destructive-ghost"
                onClick={handleDeleteCampaign}
              >
                Delete
              </Button>
            </>
          ) : (
            <>
              <span className="font-medium text-foreground text-sm flex items-center gap-2">
                <LockIcon className="size-4" />
                Wait for set up to finish!
              </span>
              <span className="font-medium text-foreground/80 text-xs text-center text-balance">
                This may take a few minutes depending on the campaign size.
              </span>
              <Button
                variant="outline"
                className="mt-2"
                scope={false}
                onClick={() => navigate("/dashboard/")}
              >
                <RefreshCcw /> Refresh
              </Button>
            </>
          )}
        </div>
      )}
      {onboarding && (
        <Tooltip>
          <TooltipTrigger className="absolute -left-4 -top-4">
            <Button
              variant="ghost"
              size="icon"
              className="size-8"
              scope={false}
            >
              <InfoIcon className="bg-background rounded-full" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p className="max-w-48 text-center">
              On-Boarding campaign is not counted towards your plan's limit.
            </p>
          </TooltipContent>
        </Tooltip>
      )}
      <div className="bg-background group-hover/card:bg-background/40 transition-colors duration-150 w-full h-full rounded-[calc(var(--radius-2xl)-2px)] border">
        {/* Header */}
        <div className="w-full flex items-center justify-between p-5 pt-4">
          <LoadingOr loading={loading} skeleton="w-32 h-4">
            <span className="font-medium line-clamp-1 text-sm truncate">
              {campaign_name}
            </span>
          </LoadingOr>
          <LoadingOr loading={loading} skeleton="h-5 w-16">
            <div className="flex items-center gap-1.5">
              {isRepopulating && (
                <Badge className="text-xs gap-1 bg-slate-500/10 text-slate-500">
                  <Spinner className="size-3" />
                  Finding creators
                </Badge>
              )}
              <Badge className={`text-xs ${badgeClass}`}>
                {isPopulating ? "Setting up" : capitalize(status)}
              </Badge>
            </div>
          </LoadingOr>
        </div>

        {/* Stats */}
        <div className="w-full px-4 flex flex-col gap-5">
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="flex items-center gap-2">
              <LoadingOr loading={loading} skeleton="h-4 w-16">
                <DollarSign className="h-4 w-4 text-muted-foreground" />
                {typeof partnership === "string"
                  ? capitalize(partnership)
                  : partnership?.tiers?.length
                    ? `${partnership.tiers.length} tier${partnership.tiers.length > 1 ? "s" : ""}`
                    : "N/A"}
              </LoadingOr>
            </div>
            <div className="flex items-center gap-2">
              <LoadingOr loading={loading} skeleton="h-4 w-28">
                <Users className="h-4 w-4 text-muted-foreground" />
                {number_of_creators} creators
              </LoadingOr>
            </div>
            <div className="flex items-center gap-2">
              <LoadingOr loading={loading} skeleton="h-4 w-24">
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
                {formatFollowerCount(followers.from)} -{" "}
                {formatFollowerCount(followers.to)} followers
              </LoadingOr>
            </div>
            <div className="flex items-center gap-2">
              <LoadingOr loading={loading} skeleton="h-4 w-24">
                <img
                  src={PLATFORM_ICONS[platform] || tiktok}
                  alt={platform}
                  className="h-3.75 w-auto shrink-0"
                />
                {capitalize(platform)}
              </LoadingOr>
            </div>
          </div>
          <LoadingOr loading={loading} skeleton="h-1 w-full">
            <Progress value={progress} className="h-1" />
          </LoadingOr>
        </div>
      </div>

      {/* Footer */}
      <div className="p-1.5 py-1 w-full rounded-lg flex flex-row items-center justify-between">
        <div className="w-full flex justify-start gap-1.5 items-center min-w-0">
          <LoadingOr
            loading={loading}
            skeleton="size-8.5 rounded-md shrink-0"
          >
            <Avatar variant="square">
              <AvatarImage src={owner?.avatar} alt={owner?.first_name} />
              <AvatarFallback>
                {owner?.first_name} {owner?.last_name}
              </AvatarFallback>
            </Avatar>
          </LoadingOr>
          <div className="flex flex-col items-start justify-center min-w-0 flex-1">
            <LoadingOr loading={loading} skeleton="h-3 w-32">
              <span className="text-sm font-medium truncate w-full text-start">
                {owner?.first_name} {owner?.last_name}
              </span>
            </LoadingOr>
            <LoadingOr loading={loading} skeleton="h-2 mt-1 w-16">
              <Tooltip>
                <TooltipTrigger>
                  <span className="text-[11px] text-muted-foreground font-normal hover:underline">
                    {timeAgo(creation_date)}
                  </span>
                </TooltipTrigger>
                <TooltipContent sideOffset={-5}>
                  {new Date(creation_date).toLocaleString()}
                </TooltipContent>
              </Tooltip>
            </LoadingOr>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {is_eve && (
            <StatusIndicator
              loading={loading}
              icon={FlameIcon}
              color="primary"
              tooltip="Run by Amy — manage this campaign from the Amy chat."
            />
          )}
          <StatusIndicator
            loading={loading}
            icon={messaging === "automatic" ? SendIcon : EyeIcon}
            color={messaging === "automatic" ? "success" : "warning"}
            tooltip={
              messaging === "automatic"
                ? "Messaging is automatic."
                : "Messaging requires manual approval."
            }
          />
          <StatusIndicator
            loading={loading}
            icon={(is_eve ? eve_autopilot : autopilot) ? PlayCircleIcon : PauseIcon}
            color={(is_eve ? eve_autopilot : autopilot) ? "success" : "warning"}
            tooltip={
              is_eve
                ? eve_autopilot
                  ? "Amy's automation is running."
                  : "Amy's automation is paused."
                : autopilot
                  ? "Campaign is on Autopilot."
                  : "Campaign is paused."
            }
          />
          {creator_prompt === "dev!" && (
            <StatusIndicator
              loading={loading}
              icon={Code2Icon}
              tooltip="This campaign has development creators."
              color="muted-foreground"
            />
          )}
        </div>
      </div>
    </Button>
  );
}
